<template>
  <div class="ab__registration">
    <h2 v-if="isEditMode">{{ $t('ab.addOrEdit.edit.title') }}</h2>
    <h2 v-else>{{ $t('ab.addOrEdit.registration.title') }}</h2>

    <div class="ab-page__set">
      <div class="a-page-info--wrap">
        <div class="label">
          <i>A</i>
        </div>
        <div class="info">
          <div class="input--wrap">
            <label for="aPageName">
              {{ $t('ab.addOrEdit.registration.label.aPageName') }}
            </label>
            <input id="aPageName" type="text" readonly class="readonly" :value="applyData.pageName">
          </div>
          <div class="input--wrap">
            <label for="aPageUrl">
              {{ $t('ab.addOrEdit.registration.label.aPageUrl') }}
            </label>
            <input id="aPageUrl" type="text" readonly class="readonly" :value="applyData.url">
          </div>
        </div>
      </div>

      <div class="b-page-info--wrap">
        <div class="label">
          <i>B</i>
        </div>
        <div class="info">
          <div class="input--wrap">
            <label for="bPageName">
               {{ $t('ab.addOrEdit.registration.label.bPageName') }}
            </label>
            <input id="bPageName" type="text"
                   v-model.trim="model.pageName"
                   :class="{error: validation.bPageNameNull}">
            <i v-if="model.pageName.length"
               class="icon icon--close14"
               @click="model.pageName = ''"></i>
            <p v-if="validation.bPageNameNull" class="validation__text">
              {{ $t('common.validationErrorText.ab.pageName.required') }}
            </p>
          </div>
          <div class="input--wrap">
            <label for="bPageUrl">
               {{ $t('ab.addOrEdit.registration.label.bPageUrl') }}
            </label>
            <input v-if="isEditMode" type="text" class="readonly" :value="model.url" readonly>
            <input v-else id="bPageUrl" type="text"
                   v-model.trim="model.url"
                   :class="{error: validation.bPageUrlFormatError || validation.bPageUrlNull || validation.DomainNotMatch}">
            <i v-if="model.url.length && !isEditMode"
               class="icon icon--close14"
               @click="model.url = ''"></i>
            <p v-if="validation.bPageUrlNull || validation.bPageUrlFormatError" class="validation__text">
              {{ $t('common.validationErrorText.ab.pageUrl.required') }}
            </p>
            <p v-if="validation.DomainNotMatch" class="validation__text"
              v-html="$t('common.validationErrorText.ab.pageUrl.domainMatch')"></p>
          </div>
        </div>
      </div>
    </div>

    <div class="ab-traffic__set">
      <p class="desc"
         v-html="$t('ab.addOrEdit.registration.traffic.desc', { remainTraffic: availableTraffic })"></p>
      <div class="fig--wrap">
        <span class="label">{{ $t('ab.addOrEdit.registration.traffic.label') }}</span>
        <div class="input--wrap type__range">
          <div class="range">
            <label for="aPageTraffic">A</label>
            <range-slider
              id="aPageTraffic"
              class="slider a-page"
              :class="{ readonly: isEditMode }"
              :disabled="isEditMode"
              :min="0"
              :max="isEditMode ? 50 : availableTraffic / 2"
              :step="1"
              :value="halfTraffic"
              @input="onSliderInput"/>
          </div>
          <div class="range">
            <label for="bPageTraffic">B</label>
            <range-slider
              id="bPageTraffic"
              class="slider b-page"
              :class="{ readonly: isEditMode }"
              :disabled="isEditMode"
              :min="0"
              :max="isEditMode ? 50 : availableTraffic / 2"
              :step="1"
              :value="halfTraffic"
              @input="onSliderInput"/>
          </div>
        </div>
        <i class="icon icon--link-red-size20"></i>

        <div v-if="isEditMode" class="input--wrap type__number">
          <input type="number"
                 min="0"
                 max="100"
                 class="readonly"
                 readonly="readonly"
                 :value="model.traffic"> %
        </div>
        <div v-else class="input--wrap type__number">
          <input type="number"
                 min="0"
                 :max="availableTraffic"
                 v-model.number="model.traffic"> %
          <span>/ {{ availableTraffic }}%</span>
        </div>
        <figure class="fig-traffic-graph--wrap">
          <app-chart chartType="pie"
            class="pie-chart"
            :width="180"
            :height="180"
            :chartOptions="isEditMode ? pieChartOptionsTypeEdit : pieChartOptionsTypeAdd">
          </app-chart>
        </figure>
        <ul class="legend--wrap">
          <li v-if="!isEditMode">
            <i class="circle color-used"></i>
            <span class="value">{{ abUsedTraffic }}%</span>
            <span>{{ $t('ab.addOrEdit.registration.traffic.existingTraffic.label') }}</span>
            <ui-icon-tooltip iconSize="normal" arrowBottom arrowRight>
              <span v-html="$t('ab.addOrEdit.registration.traffic.existingTraffic.tooltip')"></span>
            </ui-icon-tooltip>
          </li>
          <li>
            <i class="circle color-a-page"></i>
            <span class="value">{{ halfTraffic }}%</span>
            <span>A {{ $t('common.page') }}</span>
          </li>
          <li>
            <i class="circle color-b-page"></i>
            <span class="value">{{ halfTraffic }}%</span>
            <span>B {{ $t('common.page') }}</span>
          </li>
        </ul>
      </div>
      <p class="emphysize" v-html="$t('ab.addOrEdit.registration.traffic.emphysize')"></p>
    </div>

    <div class="button--wrap">
      <ui-button class="btn__cancel" size="large" @click="onAddOrEditCancel">{{ $t('common.cancel') }}</ui-button>

      <ui-button v-if="isEditMode" class="btn__register" size="large" primary @click="onModify">{{ $t('common.edit') }}</ui-button>
      <ui-button v-else
                 class="btn__register"
                 primary
                 size="large"
                 :disabled="!hasTraffic"
                 :class="{disabled: !hasTraffic}"
                 @click="onRegistration">
        {{ $t('common.register') }}
      </ui-button>
    </div>

    <!-- 트래킹 코드 -->
    <TrackingCodeConfirm
      v-if="showTrackingCode"
      @closePopup="showTrackingCode = false"
      :sid="applyData.sid">
      <button class="btn__confirm" @click="onAddorEditDone()" slot="somethingAction">
        <span class="btn-text">{{ $t('common.confirm') }}</span>
      </button>
    </TrackingCodeConfirm>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { addPage, modifyPage } from '@/api/abtest'
import { isValidUrl } from '@/libs/validation-helper'
import AppChart from '@/components/chart'
import TrackingCodeConfirm from '@/components/dashboard/TrackingCodeConfirm.vue'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import { getDialog } from '@/libs/vue-utils'
import UiButton from '@/components/ui/UiButton'

import RangeSlider from 'vue-range-slider'
import 'vue-range-slider/dist/vue-range-slider.scss'

const openDialog = getDialog('customData')

export default {
  name: 'ABAddOrEdit',
  components: {
    RangeSlider,
    AppChart,
    TrackingCodeConfirm,
    UiIconTooltip,
    UiButton
  },
  data () {
    return {
      showTrackingCode: false,
      validation: {
        bPageNameNull: false,
        bPageUrlNull: false,
        bPageUrlFormatError: false,
        DomainNotMatch: false
      },
      model: {
        pageName: '',
        url: '',
        traffic: 0
      },
      abAid: null
    }
  },
  created () {
    if (this.isEditMode) {
      this.abAid = this.$route.params.abAid
      const bData = this.abList.find(item => item.aid === this.abAid)
      this.model.pageName = bData.pageName
      this.model.url = bData.url
      this.model.traffic = bData.traffic
    } else {
      if (this.availableTraffic <= 20) { //  in AB page add mode, set traffic default value
        this.model.traffic = this.availableTraffic
      } else {
        this.model.traffic = 20
      }
    }
  },
  computed: {
    ...mapState([
      'applyData',
      'abList'
    ]),
    ...mapGetters([
      'abUsedTraffic'
    ]),
    aid () {
      return this.$route.params.aid
    },
    isEditMode () {
      return this.$route.name === 'ab.edit'
    },
    availableTraffic () {
      return 100 - this.abUsedTraffic
    },
    hasTraffic () {
      return this.model.traffic
    },
    halfTraffic () {
      return this.model.traffic ? this.model.traffic / 2 : 0
    },
    pieChartOptionsTypeAdd () {
      // tooltip 사용하지 않으므로 i18n 적용 불필요
      return {
        tooltip: {
          show: false
        },
        legend: {
          show: false
        },
        emphasis: {
          show: false
        },
        series: [
          {
            name: '트래픽',
            type: 'pie',
            radius: ['30%', '100%'],
            avoidLabelOverlap: false,
            label: {
              normal: {
                show: false
              },
              emphasis: {
                show: false
              }
            },
            labelLine: {
              normal: {
                show: false
              }
            },
            data: [
              { value: this.abUsedTraffic, name: '사용중' },
              { value: this.halfTraffic, name: 'A페이지' },
              { value: this.halfTraffic, name: 'B페이지' },
              { value: 100 - this.abUsedTraffic - this.model.traffic, name: '가용 트래픽' }
            ]
          }
        ],
        color: ['#555555', '#9fce12', '#1199af', '#ebebeb']
      }
    },
    pieChartOptionsTypeEdit () {
      // tooltip 사용하지 않으므로 i18n 적용 불필요
      return {
        tooltip: {
          show: false
        },
        legend: {
          show: false
        },
        emphasis: {
          show: false
        },
        series: [
          {
            name: '트래픽',
            type: 'pie',
            radius: ['30%', '100%'],
            avoidLabelOverlap: false,
            label: {
              normal: {
                show: false
              },
              emphasis: {
                show: false
              }
            },
            labelLine: {
              normal: {
                show: false
              }
            },
            data: [
              { value: this.halfTraffic, name: 'A페이지' },
              { value: this.halfTraffic, name: 'B페이지' },
              { value: 100 - this.model.traffic, name: '가용 트래픽' }
            ]
          }
        ],
        color: ['#9fce12', '#1199af', '#ebebeb']
      }
    },
    validationUrl () {
      return isValidUrl(this.model.url)
    },
    validationDomain () {
      let originalDomain = this.applyData.real_domain.replace('www.', '')
      let parser = document.createElement('a')
      parser.href = this.model.url
      let hostname = parser.hostname.replace('www.', '')
      return originalDomain === hostname
    }
  },
  watch: {
    'model.traffic' (value) {
      if (!this.isEditMode && value > this.availableTraffic) {
        this.model.traffic = this.availableTraffic
      }
    }
  },
  methods: {
    ...mapActions([
      'storeABList'
    ]),
    onSliderInput (value) {
      this.model.traffic = value * 2
    },
    getValicationErrorText (errorMessage) {
      const keyPrefix = 'common.validationErrorText.ab.addPage'
      let messageKey

      switch (errorMessage) {
        case 'NOEXIST':
          messageKey = `${keyPrefix}.notExists`
          break
        case 'INVALID URL':
          messageKey = `${keyPrefix}.invalidUrl`
          break
        case 'FULL TRAFFIC':
          messageKey = `${keyPrefix}.fullTraffic`
          break
        case 'URL ERROR':
          messageKey = `${keyPrefix}.urlError`
          break
        case 'EXIST URL':
          messageKey = `${keyPrefix}.existUrl`
          break
        default :
          messageKey = 'common.validationErrorText.error'
      }
      return this.$t(messageKey)
    },
    onRegistration () {
      if (!this.model.pageName) {
        this.validation.bPageNameNull = true
        return false
      } else {
        this.validation.bPageNameNull = false
      }

      if (!this.model.url) {
        this.validation.bPageUrlNull = true
        return false
      } else {
        this.validation.bPageUrlNull = false
      }

      if (!this.validationUrl) {
        this.validation.bPageUrlFormatError = true
        return false
      } else {
        this.validation.bPageUrlFormatError = false
      }

      if (!this.validationDomain) {
        this.validation.DomainNotMatch = true
        return false
      } else {
        this.validation.DomainNotMatch = false
      }

      const values = {
        aid: this.applyData._id,
        url: this.model.url,
        pageName: this.model.pageName,
        traffic: this.model.traffic
      }

      // todo: i18n
      addPage(values)
        .then(() => {
          this.showTrackingCode = true
        })
        .catch(err => {
          const errorMessage = this.getValicationErrorText(err.message)
          openDialog({
            type: 'alert',
            iconClass: 'icon--dialog-warning',
            title: this.$t('dialog.common.title'),
            description: errorMessage
          })
        })
    },
    onModify () {
      if (!this.model.pageName) {
        this.validation.bPageNameNull = true
        return false
      } else {
        this.validation.bPageNameNull = false
      }
      const values = {
        orgAid: this.aid,
        abAid: this.abAid,
        pageName: this.model.pageName,
        status: this.model.status
      }
      modifyPage(values).then(res => {
        this.onAddorEditDone()
      }).catch(err => {
        const errorMessage = this.getValicationErrorText(err.message)
        openDialog({
          type: 'alert',
          iconClass: 'icon--dialog-warning',
          title: this.$t('dialog.common.title'),
          description: errorMessage
        })
      })
    },
    onAddorEditDone () {
      this.storeABList(this.aid).then(() => {
        this.$router.push({ name: 'ab.list', params: this.$route.params })
      }).catch(console.error)
    },
    onAddOrEditCancel () {
      this.$router.push({ name: 'ab.list', params: { aid: this.$route.params.aid } })
    }
  }
}
</script>

<style lang="scss" scoped>
.range-slider {
  &.readonly {
    pointer-events: none;
    opacity: 0.3;
  }

  /deep/ .range-slider-rail {
    width: 125px;
    height: 8px;
    border-radius: 4px;
    background-color: #d7d7d7;
  }
  /deep/ .range-slider-fill {
    height: 8px;
    border-radius: 4px;
  }
  /deep/ .range-slider-knob {
    width: 18px;
    height: 18px;
    box-shadow: 0 0 1px 0 rgba(0, 0, 0, 0.29), 0 2px 3px 0 rgba(0, 0, 0, 0.22);
    background-color: #ffffff;
  }
  &.a-page /deep/ .range-slider-fill {
    background-color: #9fce12;
  }

  &.b-page /deep/ .range-slider-fill{
    background-color: #1199af;
  }
}

.ab__registration {
  box-sizing: border-box;
  padding: 30px;
  overflow-y: auto;

  h2 {
    font-size: 22px;
    font-weight: bold;
    color: #444444;
  }

  .ab-page__set {
    margin-top: 30px;
    display: flex;
    justify-content: space-between;

    .validation__text {
      @include validErrorText;
      transform: translate(5px, 5px);
      line-height: 1.46;
    }

    .a-page-info--wrap, .b-page-info--wrap {
      position: relative;
      width: calc(50% - 15px);
      border-radius: 14px;
      box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
      background-color: #ffffff;
      padding: 25px;
      padding-left: 125px;
      box-sizing: border-box;

      .label {
        position: absolute;
        top: 25px;
        left: 25px;
        width: 75px;
        height: 75px;
        border-radius: 7px;

        i {
          @include positionXYcenter;
          font-size: 30px;
          font-weight: bold;
          color: #ffffff;
        }
      }

      .input--wrap {
        &:nth-last-of-type(1) {
          margin-top: 40px;
        }

        label {
          display: block;
          font-size: 13px;
          font-weight: bold;
          color: #767676;
        }
        input {
          border-radius: 6px;
          width: 100%;
          height: 38px;
          margin-top: 5px;
          box-sizing: border-box;
          font-size: 14px;
          padding-left: 12px;
          padding-right: 12px;

          &.error {
            border-color: $errorColor !important;
          }
        }
      }

      &.a-page-info--wrap {
        .label {
          background-color: #9fce12;
        }
        .input--wrap {
          input {
            border: solid 1px #d7d7d7;
            background-color: #e6e6e6;
            color: #999999;
          }
        }
      }

      &.b-page-info--wrap {
        .label {
          background-color: #1199af;
        }
        .input--wrap {
          position: relative;

          input {
            border: solid 1px #bbbbbb;
            background-color: #ffffff;
            color: #2f2f2f;

            &:focus {
              border-color: #289eef;
            }

            &.readonly {
              border: solid 1px #d7d7d7;
              background-color: #e6e6e6;
              color: #999999;
            }
          }

          .icon--close14 {
            position: absolute;
            top: 21px;
            right: 2px;
            padding: 9px 12px;
            cursor: pointer;
            background-position: center;
            background-color: transparent;
          }
        }
      }
    }
  }

  .ab-traffic__set {
    margin-top: 30px;
    width: 100%;
    border-radius: 14px;
    box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
    background-color: #ffffff;
    padding: 25px;
    padding-bottom: 32px;
    box-sizing: border-box;

    .desc {
      font-size: 14px;
      font-weight: bold;
      line-height: 1.5;
      color: #767676;
    }

    .fig--wrap {
      margin-top: 25px;
      display: flex;
      justify-content: center;
      align-items: center;

      .label {
        font-size: 14px;
        font-weight: bold;
        line-height: 1;
        color: #888888;
      }

      .input--wrap.type__range {
        margin-left: 35px;
        .range {
          padding-left: 23px;
          position: relative;
          font-size: 14px;
          font-weight: bold;
          line-height: 1;
          color: #444444;

          &:nth-last-of-type(1) {
            margin-top: 15px;
          }

          label {
            @include positionYcenter;
            left: 0;
          }
        }
      }

      .input--wrap.type__number {
        margin-left: 15px;
        font-size: 14px;
        color: #2f2f2f;

        input {
          display: inline-block;
          width: 58px;
          height: 38px;
          border-radius: 6px;
          border: solid 1px #bbbbbb;
          background-color: #ffffff;
          box-sizing: border-box;
          padding-left: 15px;

          &.readonly {
            pointer-events: none;
            border: solid 1px #d7d7d7;
            background-color: #e6e6e6;
            color: #999;
          }
        }

        span {
          display: inline-block;
          margin-left: 8px;
          color: #767676;
        }
      }

      .fig-traffic-graph--wrap {
        position: relative;
        margin-left: 32px;

        /deep/ .pie-chart {
          pointer-events: none;
        }
      }

      .legend--wrap {
        white-space: nowrap;

        li {
          margin-top: 18px;
          margin-left: 26px;
          font-size: 13px;
          color: #444;

          &:nth-of-type(1) {
            position: relative;
            margin-top: 0;

            .tooltip {
              position: absolute;
              top: 0;
              left: 100%;
              margin-left: 4px;
            }
          }

          .circle {
            display: inline-block;
            width: 11px;
            height: 11px;
            background-color: #555555;
            border-radius: 50%;
            margin-right: 9px;

            &.color-used {
              background-color: #555;
            }
            &.color-a-page {
              background-color: #9fce12;
            }
            &.color-b-page {
              background-color: #1199af;
            }
          }

          .value {
            display: inline-block;
            margin-right: 11px;
          }
        }
      }
    }

    .emphysize {
      margin-top: 25px;
      text-align: center;
      font-size: 13px;
      line-height: 1.54;
      color: $brandColor;
    }
  }

  .button--wrap {
    margin-top: 30px;
    text-align: center;

    button {
      &.btn__register {
        margin-left: 5px;
      }
    }
  }

  .btn__confirm {
    background-color: $brandColor;
    border: 0 !important;
    margin-left: 5px;

    &:hover {
      background-color: $brandColor !important;
    }

    .btn-text {
      color: #fff;
    }
  }
}
</style>
